import { LogOut, UploadCloud } from "lucide-react";
import { useState } from "react";
import { trpc } from "../../../lib/trpc";
import { getAccountCopy } from "../../data/accountCopy";
import { useTranslation } from "../../i18n";
import { Alert, Button, ButtonLink, LoadingState, Panel } from "../ui";
import { ProjectLibrary } from "./ProjectLibrary";
import { SignedOutPanel } from "./SignedOutPanel";
import { UploadLibrary } from "./UploadLibrary";
import type { UploadRow } from "./types";
import { useOffline } from "./useOffline";
import "../../styles/account-workspace.css";

export function MyPageView() {
	const { locale, localizedPath } = useTranslation();
	const copy = getAccountCopy(locale).myPage;
	const offline = useOffline();
	const meQuery = trpc.auth.me.useQuery(undefined, { retry: false, networkMode: "always" });
	const uploadsQuery = trpc.library.listMine.useQuery(undefined, {
		enabled: !!meQuery.data,
		retry: false,
		networkMode: "always",
	});
	const [loggingOut, setLoggingOut] = useState(false);
	const [logoutError, setLogoutError] = useState(false);
	const logout = async () => {
		setLoggingOut(true);
		setLogoutError(false);
		try {
			const response = await fetch("/share/auth/logout", { method: "POST", credentials: "include" });
			if (!response.ok) throw new Error("Logout failed");
			window.location.href = localizedPath("/");
		} catch {
			setLogoutError(true);
			setLoggingOut(false);
		}
	};
	return (
		<main id="main-content" className="account-workspace">
			{(meQuery.isLoading || meQuery.error || meQuery.data) && (
				<header className="account-page-heading">
					<div>
						<h1>{copy.title}</h1>
						<p>
							{locale === "ja"
								? "モデルをプロジェクトにまとめて共有したり、配信方法や公開設定を変更できます。"
								: "Group models into projects and manage delivery and sharing settings."}
						</p>
					</div>
					{meQuery.data && (
						<div className="account-heading-actions">
							<ButtonLink href={`${localizedPath("/")}#upload`}>
								<UploadCloud size={16} />
								{copy.upload}
							</ButtonLink>
						</div>
					)}
				</header>
			)}
			{meQuery.data && (
				<div className="account-identity">
					<span className="account-avatar" aria-hidden="true">
						{(meQuery.data.name || meQuery.data.email).slice(0, 1).toUpperCase()}
					</span>
					<p>{meQuery.data.email}</p>
					<Button onClick={() => void logout()} variant="ghost" size="sm" disabled={loggingOut}>
						<LogOut size={14} />
						{loggingOut ? copy.loggingOut : copy.logout}
					</Button>
				</div>
			)}
			{offline && (
				<div className="mb-4">
					<Alert tone="warning">{copy.offline}</Alert>
				</div>
			)}
			{logoutError && (
				<div className="mb-4">
					<Alert tone="error">{copy.logoutFailed}</Alert>
				</div>
			)}
			{meQuery.isLoading ? (
				<Panel className="p-6">
					<LoadingState label={copy.checking} />
				</Panel>
			) : meQuery.error ? (
				<Panel className="space-y-4 p-6">
					<Alert tone="error" title={copy.checkFailedTitle}>
						{copy.checkFailedBody}
					</Alert>
					<Button onClick={() => void meQuery.refetch()} variant="secondary" disabled={meQuery.isFetching}>
						{copy.retry}
					</Button>
				</Panel>
			) : !meQuery.data ? (
				<SignedOutPanel />
			) : uploadsQuery.error ? (
				<Panel className="space-y-4 p-6">
					<Alert tone="error">{copy.linksFailed}</Alert>
					<Button onClick={() => void uploadsQuery.refetch()} variant="secondary" disabled={uploadsQuery.isFetching}>
						{copy.retry}
					</Button>
				</Panel>
			) : (
				<>
					<ProjectLibrary uploads={(uploadsQuery.data ?? []) as UploadRow[]} loading={uploadsQuery.isLoading} />
					<UploadLibrary uploads={(uploadsQuery.data ?? []) as UploadRow[]} loading={uploadsQuery.isLoading} />
				</>
			)}
		</main>
	);
}
